// Client-side tree over the vault's flat key list. The list endpoint returns
// every secret path under the mount (recursively, "/"-separated), so grouping
// by path segment needs no API support.

export interface VaultTreeNode {
  /** Last path segment (the row label). */
  name: string;
  /** Full prefix path of this group, e.g. "team/api". Empty for the root. */
  path: string;
  /** Child groups, sorted by name. Rendered before leaves. */
  groups: VaultTreeNode[];
  /** Full secret paths stored directly in this group, sorted by last segment. */
  leaves: string[];
  /** Secrets beneath this group at any depth. */
  leafCount: number;
}

/** Secrets at or below this count start fully expanded; above it, collapsed. */
export const EXPAND_ALL_THRESHOLD = 30;

function segmentsOf(key: string): string[] {
  return key.split("/").filter((s) => s.length > 0);
}

function lastSegment(key: string): string {
  const segments = segmentsOf(key);
  return segments[segments.length - 1] ?? key;
}

export function buildVaultTree(keys: string[]): VaultTreeNode {
  const root: VaultTreeNode = { name: "", path: "", groups: [], leaves: [], leafCount: 0 };
  const childIndex = new Map<VaultTreeNode, Map<string, VaultTreeNode>>();
  const seen = new Set<string>();

  for (const key of keys) {
    if (seen.has(key)) continue;
    seen.add(key);
    const segments = segmentsOf(key);
    if (segments.length === 0) continue;

    let node = root;
    node.leafCount += 1;
    for (let i = 0; i < segments.length - 1; i++) {
      let byName = childIndex.get(node);
      if (!byName) {
        byName = new Map();
        childIndex.set(node, byName);
      }
      let child = byName.get(segments[i]);
      if (!child) {
        child = {
          name: segments[i],
          path: segments.slice(0, i + 1).join("/"),
          groups: [],
          leaves: [],
          leafCount: 0,
        };
        byName.set(segments[i], child);
        node.groups.push(child);
      }
      child.leafCount += 1;
      node = child;
    }
    node.leaves.push(key);
  }

  sortTree(root);
  return root;
}

function sortTree(node: VaultTreeNode): void {
  node.groups.sort((a, b) => a.name.localeCompare(b.name));
  node.leaves.sort((a, b) => lastSegment(a).localeCompare(lastSegment(b)));
  for (const g of node.groups) sortTree(g);
}

/** Case-insensitive substring match over the full path. */
export function filterVaultKeys(keys: string[], query: string): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return keys;
  return keys.filter((k) => k.toLowerCase().includes(q));
}

/** Every group path that contains one of the given keys, at any depth. */
export function ancestorGroups(keys: string[]): Set<string> {
  const out = new Set<string>();
  for (const key of keys) {
    const segments = segmentsOf(key);
    for (let i = 1; i < segments.length; i++) {
      out.add(segments.slice(0, i).join("/"));
    }
  }
  return out;
}

export function allGroupPaths(node: VaultTreeNode): string[] {
  const out: string[] = [];
  const walk = (n: VaultTreeNode) => {
    for (const g of n.groups) {
      out.push(g.path);
      walk(g);
    }
  };
  walk(node);
  return out;
}

/** Initial expand state: everything open for small vaults, root-only otherwise. */
export function defaultExpanded(root: VaultTreeNode): Set<string> {
  return root.leafCount <= EXPAND_ALL_THRESHOLD ? new Set(allGroupPaths(root)) : new Set();
}

export function vaultLeafLabel(key: string): string {
  return lastSegment(key);
}
